import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, makeStyles } from '@material-ui/core';
import { CustomizedTypography } from '@components/Customized';
import ProfileUpdateInput from './ProfileUpdateInput';
import BizTypeRadioGroup from './BizTypeRadioGroup';
import { CapaQuotationUserBizType } from '@generated/graphql';
import { colorSet } from '@components/Provider';

interface ProfileUpdateFormProps {
  setOpenPostCodeDialog: () => void;
  bizType: CapaQuotationUserBizType;
  setBizType: (bizType: CapaQuotationUserBizType) => void;
}

const ProfileUpdateForm: React.FC<ProfileUpdateFormProps> = ({
  setOpenPostCodeDialog,
  bizType,
  setBizType
}) => {
  const { register, errors, clearErrors, watch, trigger } = useFormContext();
  const isIndividual = bizType === CapaQuotationUserBizType.Individual;
  const { container, organizationName } = useStyles({ isIndividual });
  const companyRegistrationNumber = watch('companyRegistrationNumber');

  useEffect(() => {
    if (errors?.['companyRegistrationNumber']) {
      clearErrors('companyRegistrationNumber');
      trigger();
    }
    //eslint-disable-next-line
  }, [companyRegistrationNumber]);

  return (
    <Box className={container}>
      <BizTypeRadioGroup
        bizType={bizType}
        setBizType={(bizType: CapaQuotationUserBizType) => setBizType(bizType)}
      />

      {/* 이름 */}
      <ProfileUpdateInput
        name="managerName"
        label="이름"
        placeholder="특수문자 없이 20자 이내로 입력해주세요."
        type="text"
        inputProps={{
          ref: register({
            pattern: {
              value: /^[ㄱ-ㅎ|가-힣|a-z|A-Z|0-9|]+$/,
              message: '이름은 특수문자 없이 20자 이내로 입력하셔야 합니다.'
            },
            maxLength: {
              value: 20,
              message: '이름은 특수문자 없이 20자 이내로 입력하셔야 합니다.'
            },
            minLength: {
              value: 1,
              message: '이름은 특수문자 없이 20자 이내로 입력하셔야 합니다.'
            },
            required: {
              value: true,
              message: '특수문자 없이 20자 이내로 입력해주세요.'
            }
          })
        }}
        error={errors['managerName'] ? true : false}
        helperText={errors['managerName']?.message}
      />

      {/* 휴대전화 */}
      <ProfileUpdateInput
        name="cellphoneNumber"
        label="휴대전화"
        placeholder="휴대전화"
        subLabel={
          <CustomizedTypography variant="body2" className="gray600">
            -(하이픈) 제외하고 숫자만 입력
          </CustomizedTypography>
        }
        inputProps={{
          maxLength: 11,
          onChange: ({ currentTarget }) => {
            currentTarget.value = (currentTarget.value ?? '').replace(
              /[^0-9]/g,
              ''
            );
            if (errors['cellphoneNumber']) {
              trigger('cellphoneNumber');
            }
          },
          ref: register({
            maxLength: {
              value: 11,
              message: '-(하이픈)을 제외한 11자리 숫자로 입력해주세요'
            },
            minLength: {
              value: 11,
              message: '-(하이픈)을 제외한 11자리 숫자로 입력해주세요'
            },
            required: { value: true, message: '휴대전화번호를 입력해주세요.' }
          })
        }}
        error={errors['cellphoneNumber'] ? true : false}
        helperText={errors['cellphoneNumber']?.message}
      />

      {/* 회사 / 단체명 */}
      {bizType === CapaQuotationUserBizType.Biz && (
        <ProfileUpdateInput
          name="organizationName"
          className={organizationName}
          label={'회사명'}
          placeholder={'회사명'}
          type="text"
          inputProps={{
            disabled: isIndividual,
            ref: register({
              required: {
                value: !isIndividual,
                message: `회사명을 입력해주세요.`
              }
            })
          }}
          error={errors['organizationName'] ? true : false}
          helperText={errors['organizationName']?.message}
        />
      )}

      {/* 주소 */}
      <ProfileUpdateInput
        name="fullAddress"
        label="주소"
        placeholder="주소"
        subLabel={
          <CustomizedTypography variant="body2" className="gray600">
            제조 파트너에게는 도/시 단위까지 표시 됩니다.
          </CustomizedTypography>
        }
        type="text"
        inputProps={{
          ref: register({
            required: { value: true, message: '주소를 입력해주세요.' }
          }),
          onClick: setOpenPostCodeDialog,
          readOnly: true
        }}
        error={errors['fullAddress'] ? true : false}
        helperText={errors['fullAddress']?.message}
      />

      {/* 사업자등록번호 */}
      {bizType === CapaQuotationUserBizType.Biz && (
        <ProfileUpdateInput
          name="companyRegistrationNumber"
          label="사업자등록번호"
          placeholder="사업자등록번호"
          type="number"
          inputProps={{
            ref: register({ required: true })
          }}
          error={errors['companyRegistrationNumber'] ? true : false}
          helperText={errors['companyRegistrationNumber']?.message}
        />
      )}
    </Box>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    [theme.breakpoints.down('xs')]: {
      gap: '16px'
    }
  },
  organizationName: {
    '& input::placeholder': {
      color: ({ isIndividual }: any) =>
        isIndividual ? colorSet.primary800 : colorSet.gray500
    }
  }
}));

export default ProfileUpdateForm;
